import { motion, HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";
import React from "react";

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: "default" | "gold" | "success" | "warning" | "error" | "info" | "outline";
  size?: "sm" | "md" | "lg";
  animate?: boolean;
  pulse?: boolean;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = "default", size = "sm", animate = false, pulse = false, children, ...props }, ref) => {
    const variants = {
      default: "bg-anthracite text-cream border-cream/10",
      gold: "bg-gold/10 text-gold border-gold/30",
      success: "bg-success/20 text-green-400 border-success/30",
      warning: "bg-warning/20 text-amber-400 border-warning/30",
      error: "bg-error/20 text-red-400 border-error/30",
      info: "bg-info/20 text-blue-400 border-info/30",
      outline: "bg-transparent border-cream/30 text-cream",
    };

    const sizes = {
      sm: "px-2 py-0.5 text-xs",
      md: "px-3 py-1 text-sm",
      lg: "px-4 py-1.5 text-base",
    };

    if (animate) {
      return (
        <motion.span
          ref={ref}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border font-medium",
            variants[variant],
            sizes[size],
            pulse && "animate-pulse",
            className
          )}
          {...props as HTMLMotionProps<"span">}
        >
          {pulse && variant === "gold" && (
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-ping" />
          )}
          {children}
        </motion.span>
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border font-medium",
          variants[variant],
          sizes[size],
          pulse && "animate-pulse",
          className
        )}
        {...props}
      >
        {pulse && variant === "gold" && (
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-ping" />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = "Badge";

interface StatusBadgeProps {
  status: "online" | "offline" | "away" | "busy";
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const statusConfig = {
    online: { color: "bg-green-500", label: "En línea" },
    offline: { color: "bg-gray-500", label: "Desconectado" },
    away: { color: "bg-amber-500", label: "Ausente" },
    busy: { color: "bg-red-500", label: "Ocupado" },
  };

  const config = statusConfig[status];

  return (
    <div className={cn("inline-flex items-center gap-2", className)}>
      <span className="relative flex h-2.5 w-2.5">
        <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", config.color)} />
        <span className={cn("relative inline-flex rounded-full h-2.5 w-2.5", config.color)} />
      </span>
      <span className="text-sm text-beige/70">{label || config.label}</span>
    </div>
  );
}

export default { Badge, StatusBadge };
